import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, BarChart3, Loader2, Users, Lock, Unlock, CircleDot, CheckSquare, Type, Hash } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const typeIcon = (type) => {
  if (type === 'single') return CircleDot;
  if (type === 'multiple') return CheckSquare;
  if (type === 'multiple_quantity') return Hash;
  return Type;
};

export default function SurveyResultsPage() {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const getToken = () => localStorage.getItem('shrt_token');

  useEffect(() => { fetchResults(); }, [surveyId]);

  const fetchResults = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/surveys/${surveyId}/results`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setData(res.data);
    } catch { toast.error('Fehler beim Laden'); navigate('/admin/surveys'); }
    finally { setLoading(false); }
  };

  const toggleStatus = async () => {
    const newStatus = data.survey.status === 'active' ? 'closed' : 'active';
    try {
      await axios.put(`${API_URL}/api/surveys/${surveyId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success(newStatus === 'closed' ? 'Umfrage geschlossen' : 'Umfrage aktiviert');
      fetchResults();
    } catch { toast.error('Fehler'); }
  };

  if (loading) return <Layout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;
  if (!data) return null;

  const { survey, results, responses_count } = data;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <button onClick={() => navigate('/admin/surveys')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" /> Alle Umfragen
        </button>

        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="font-['Oswald'] text-3xl font-bold uppercase tracking-wide flex items-center gap-3">
              <BarChart3 className="text-primary" /> {survey.title}
            </h1>
            <p className="text-muted-foreground mt-1 flex items-center gap-3">
              <span className="flex items-center gap-1"><Users size={14} /> {responses_count} Antworten</span>
              <span className={`text-xs px-2 py-0.5 ${survey.status === 'active' ? 'bg-green-500/20 text-green-600' : 'bg-red-500/20 text-red-600'}`}>
                {survey.status === 'active' ? 'Aktiv' : 'Geschlossen'}
              </span>
            </p>
          </div>
          <Button onClick={toggleStatus} variant="outline" className="rounded-none" data-testid="toggle-survey-status">
            {survey.status === 'active' ? <><Lock size={16} className="mr-2" /> Schliessen</> : <><Unlock size={16} className="mr-2" /> Aktivieren</>}
          </Button>
        </div>

        <div className="space-y-6">
          {survey.questions.map((q, qIdx) => {
            const Icon = typeIcon(q.type);
            const result = results[q.id];
            return (
              <div key={q.id} className="bg-card border border-border p-5" data-testid={`result-question-${qIdx}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Icon size={16} className="text-primary" />
                  <h3 className="font-semibold">Frage {qIdx + 1}: {q.text}</h3>
                </div>

                {q.image_url && <img src={q.image_url} alt="" className="max-h-48 mb-4 border border-border" />}

                {(q.type === 'single' || q.type === 'multiple') && result && (
                  <div className="space-y-2">
                    {q.options.map((opt, oIdx) => {
                      const count = result.option_counts[oIdx] || 0;
                      const pct = result.total > 0 ? Math.round((count / result.total) * 100) : 0;
                      return (
                        <div key={oIdx}>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span>{opt || `Option ${oIdx + 1}`}</span>
                            <span className="text-muted-foreground">{count} ({pct}%)</span>
                          </div>
                          <div className="w-full h-6 bg-muted/50 overflow-hidden">
                            <div className="h-full bg-primary/80 transition-all duration-500" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {q.type === 'multiple_quantity' && result && (
                  <div className="space-y-2">
                    {q.options.map((opt, oIdx) => {
                      const count = result.option_counts[oIdx] || 0;
                      const qty = result.quantity_totals?.[oIdx] || 0;
                      const pct = result.total > 0 ? Math.round((count / result.total) * 100) : 0;
                      return (
                        <div key={oIdx}>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span>{opt || `Option ${oIdx + 1}`}</span>
                            <span className="text-muted-foreground">{count}x gewählt, Total: {qty} Stück</span>
                          </div>
                          <div className="w-full h-6 bg-muted/50 overflow-hidden">
                            <div className="h-full bg-primary/80 transition-all duration-500" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {q.type === 'text' && result && (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {result.texts.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Noch keine Antworten</p>
                    ) : result.texts.map((t, i) => (
                      <div key={i} className="bg-muted/30 border border-border p-3 text-sm">{t}</div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
